/** Shared analytics range presets (Cursor Usage, User Activity, etc.). */
export type ReportingRangePreset =
  | '1d'
  | '3d'
  | '1w'
  | '2w'
  | '1m'
  | '3m'
  | '6m'
  | '1y'
  | 'this-q'
  | 'last-q'
  | 'q1'
  | 'q2'
  | 'q3'
  | 'q4'
  | 'custom'
  | 'all';

export type ReportingQuarter = 1 | 2 | 3 | 4;

export interface ReportingRangePresetItem {
  value: ReportingRangePreset;
  text: string;
}

export interface ReportingRangePresetGroup {
  key: string;
  items: ReportingRangePresetItem[];
}

export interface ReportingCustomRange {
  from: string | null;
  to: string | null;
}

const RELATIVE_PRESETS: ReportingRangePresetItem[] = [
  { value: '1d', text: 'Last 1 day' },
  { value: '3d', text: 'Last 3 days' },
  { value: '1w', text: 'Last 1 week' },
  { value: '2w', text: 'Last 2 weeks' },
  { value: '1m', text: 'Last 1 month' },
  { value: '3m', text: 'Last 3 months' },
  { value: '6m', text: 'Last 6 months' },
  { value: '1y', text: 'Last 1 year' }
];

/** Flat list kept for callers/tests that only need values + labels. */
export const REPORTING_RANGE_PRESETS: ReportingRangePresetItem[] = [
  ...RELATIVE_PRESETS,
  { value: 'this-q', text: 'This quarter' },
  { value: 'last-q', text: 'Last quarter' },
  { value: 'q1', text: 'Q1' },
  { value: 'q2', text: 'Q2' },
  { value: 'q3', text: 'Q3' },
  { value: 'q4', text: 'Q4' },
  { value: 'custom', text: 'Custom range' },
  { value: 'all', text: 'All time' }
];

const PRESET_DAYS: Record<
  Exclude<
    ReportingRangePreset,
    'all' | 'custom' | 'this-q' | 'last-q' | 'q1' | 'q2' | 'q3' | 'q4'
  >,
  number
> = {
  '1d': 1,
  '3d': 3,
  '1w': 7,
  '2w': 14,
  '1m': 30,
  '3m': 90,
  '6m': 180,
  '1y': 365
};

export function isReportingRangePreset(v: unknown): v is ReportingRangePreset {
  return (
    v === '1d' ||
    v === '3d' ||
    v === '1w' ||
    v === '2w' ||
    v === '1m' ||
    v === '3m' ||
    v === '6m' ||
    v === '1y' ||
    v === 'this-q' ||
    v === 'last-q' ||
    v === 'q1' ||
    v === 'q2' ||
    v === 'q3' ||
    v === 'q4' ||
    v === 'custom' ||
    v === 'all'
  );
}

/** Local calendar day as `yyyy-MM-dd`. */
export function toLocalDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Parse `yyyy-MM-dd` into a local Date at midnight, or null if invalid. */
export function parseLocalDateKey(key: string | null | undefined): Date | null {
  if (!key) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key.trim());
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Serialize local calendar day start as UTC ISO for API from-filter. */
export function toDayStartIso(d: Date): string {
  const local = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
  return local.toISOString();
}

/** Serialize local calendar day end as UTC ISO for API to-filter. */
export function toDayEndIso(d: Date): string {
  const local = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
  return local.toISOString();
}

export function getCalendarQuarter(now = new Date()): { year: number; quarter: ReportingQuarter } {
  const quarter = (Math.floor(now.getMonth() / 3) + 1) as ReportingQuarter;
  return { year: now.getFullYear(), quarter };
}

export function shiftCalendarQuarter(
  year: number,
  quarter: ReportingQuarter,
  delta: number
): { year: number; quarter: ReportingQuarter } {
  const absolute = year * 4 + (quarter - 1) + delta;
  const nextYear = Math.floor(absolute / 4);
  const nextQuarter = ((absolute % 4) + 4) % 4;
  return { year: nextYear, quarter: (nextQuarter + 1) as ReportingQuarter };
}

export function quarterDateBounds(
  year: number,
  quarter: ReportingQuarter
): { from: Date; to: Date } {
  const startMonth = (quarter - 1) * 3;
  const from = new Date(year, startMonth, 1, 0, 0, 0, 0);
  const to = new Date(year, startMonth + 3, 0, 23, 59, 59, 999);
  return { from, to };
}

/** Grouped SelectBox source with year-aware quarter labels. */
export function buildReportingRangePresetGroups(now = new Date()): ReportingRangePresetGroup[] {
  const year = now.getFullYear();
  return [
    { key: 'Recent', items: RELATIVE_PRESETS },
    {
      key: 'Quarters',
      items: [
        { value: 'this-q', text: 'This quarter' },
        { value: 'last-q', text: 'Last quarter' },
        { value: 'q1', text: `Q1 ${year}` },
        { value: 'q2', text: `Q2 ${year}` },
        { value: 'q3', text: `Q3 ${year}` },
        { value: 'q4', text: `Q4 ${year}` }
      ]
    },
    {
      key: 'Other',
      items: [
        { value: 'custom', text: 'Custom range' },
        { value: 'all', text: 'All time' }
      ]
    }
  ];
}

/** Sensible defaults when the user first picks Custom range. */
export function defaultCustomRange(now = new Date()): { from: string; to: string } {
  const to = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const from = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 29);
  return { from: toLocalDateKey(from), to: toLocalDateKey(to) };
}

/** Map a range preset (+ optional custom dates) to optional API from/to. */
export function rangePresetToQuery(
  preset: ReportingRangePreset,
  now = new Date(),
  custom: ReportingCustomRange = { from: null, to: null }
): { from?: string; to?: string } {
  if (preset === 'all') return {};

  if (preset === 'custom') {
    const fromDate = parseLocalDateKey(custom.from);
    const toDate = parseLocalDateKey(custom.to);
    if (!fromDate || !toDate) return {};
    const start = fromDate <= toDate ? fromDate : toDate;
    const end = fromDate <= toDate ? toDate : fromDate;
    return { from: toDayStartIso(start), to: toDayEndIso(end) };
  }

  if (
    preset === 'this-q' ||
    preset === 'last-q' ||
    preset === 'q1' ||
    preset === 'q2' ||
    preset === 'q3' ||
    preset === 'q4'
  ) {
    let year: number;
    let quarter: ReportingQuarter;
    if (preset === 'this-q') {
      ({ year, quarter } = getCalendarQuarter(now));
    } else if (preset === 'last-q') {
      const current = getCalendarQuarter(now);
      ({ year, quarter } = shiftCalendarQuarter(current.year, current.quarter, -1));
    } else {
      year = now.getFullYear();
      quarter = Number(preset.slice(1)) as ReportingQuarter;
    }
    const { from, to } = quarterDateBounds(year, quarter);
    return { from: toDayStartIso(from), to: toDayEndIso(to) };
  }

  const days = PRESET_DAYS[preset];
  const to = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
  const from = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (days - 1), 0, 0, 0, 0);
  return { from: toDayStartIso(from), to: toDayEndIso(to) };
}
